import React from 'react';
import { List } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { toasty } from '../../common/forms/FormElements';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';
import { SaveButton } from '../../common/forms/SaveButton';

const syncOptions = [
    { value: 'USE_DEFAULTS', label: 'Use Contract Defaults' },
    { value: 'FORCE_BILLING', label: 'Move Timesheet to Billing' },
    { value: 'FORCE_BILLING_CHARGES', label: 'Move Timesheet to Billing: Charges' },
];

/**
 * Shared class component for syncing timesheet/contract billing.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
class SyncTimesheetContractBillingForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            timesheetId: '',
            timesheetOptions: [],
            selectedTimesheetOption: null,
            syncOption: syncOptions[0].value,
        };
    }

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => {
                this.setState({ timesheetOptions: data });
                callback(data);
            });
    }, 250);

    onTimesheetChanged = (selectedOption) => {
        this.setState({
            selectedTimesheetOption: selectedOption,
            timesheetId: selectedOption?.value ?? '',
        });
    };

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { timesheetId, syncOption, selectedTimesheetOption } = this.state;

        if (!selectedTimesheetOption || !timesheetId) {
            toasty.error('Please enter a timesheet number.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.syncTimesheetContractBilling(), {
                timesheetId,
                syncOption,
            });

            this.setState({
                timesheetId: '',
                timesheetOptions: [],
                selectedTimesheetOption: null,
                syncOption: syncOptions[0].value,
            });

            toasty.success('Timesheet data has been synchronized');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error('Error: Sync failed. Please Check Form Inputs');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            saving, timesheetOptions, selectedTimesheetOption, syncOption,
        } = this.state;
        const isSubmitDisabled = saving || !selectedTimesheetOption || !syncOption;

        return (
            <form onSubmit={this.onSubmit}>
                <ValidatedSelect
                    component={AsyncSelect}
                    label="Timesheet Number"
                    loadOptions={this.loadTimesheetOptions}
                    placeholder="Type Timesheet Number Here"
                    id="timesheetId"
                    name="timesheetId"
                    value={(timesheetOptions ?? []).find((x) => x.value === selectedTimesheetOption?.value) ?? selectedTimesheetOption}
                    onChange={this.onTimesheetChanged}
                    cacheOptions
                    required
                    error="Timesheet Number is Required."
                />

                <ValidatedSelect
                    id="syncOption"
                    name="syncOption"
                    label="Sync behaviour"
                    required
                    options={syncOptions}
                    value={syncOptions.find((x) => x.value === syncOption) ?? null}
                    onChange={(opt) => this.setState({ syncOption: opt?.value ?? syncOptions[0].value })}
                    error="Select sync behaviour"
                />

                <BlockBorder />

                <div className="d-flex justify-content-center">
                    <SaveButton
                        saving={saving}
                        disabled={isSubmitDisabled}
                        label="Submit"
                    />
                </div>
            </form>
        );
    }
}

/**
 * Renders the execution history details for SyncTimesheetContractBilling.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const SyncTimesheetContractBillingHistoryDetails = ({ record }) => (
    <>
        <div>
            <List type="unstyled">
                {record.input.timesheet_number && (
                    <li>
                        <strong>Timesheet:</strong>
                        {' '}
                        {record.input.timesheet_number}
                    </li>
                )}
                {record.input.sync_option && (
                    <li>
                        <strong>Sync behaviour:</strong>
                        {' '}
                        {record.input.sync_option}
                    </li>
                )}
                {record.input.use_invoicing && (
                    <li>
                        <strong>Billing Used Invoicing:</strong>
                        {' '}
                        {record.input.use_invoicing}
                    </li>
                )}
                {record.input.billing_status_id && (
                    <li>
                        <strong>Billing Had Status:</strong>
                        {' '}
                        {record.input.billing_status_id}
                    </li>
                )}
                {record.input.billing_id && (
                    <li>
                        <strong>Billing ID:</strong>
                        {' '}
                        {record.input.billing_id}
                    </li>
                )}
            </List>
        </div>

        <div>
            <List type="unstyled">
                {record.output.use_invoicing && (
                    <li>
                        <strong>Billing Now Uses Invoicing:</strong>
                        {' '}
                        {record.output.use_invoicing}
                    </li>
                )}
                {record.output.billing_status_id && (
                    <li>
                        <strong>New Billing Status:</strong>
                        {' '}
                        {record.output.billing_status_id}
                    </li>
                )}
            </List>
        </div>
    </>
);

export { SyncTimesheetContractBillingForm };
export default SyncTimesheetContractBillingForm;
